import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UserPlus, Search, Check, Building2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestRegister() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [purpose, setPurpose] = useState("");
  const [hostQuery, setHostQuery] = useState("");
  const [hostResults, setHostResults] = useState([]);
  const [selectedHost, setSelectedHost] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [wasPreRegistered, setWasPreRegistered] = useState(false);
  const [preRegHost, setPreRegHost] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (hostQuery.length < 2) { setHostResults([]); setShowDropdown(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      axios.get(`${API}/guests/search-hosts?q=${encodeURIComponent(hostQuery)}`)
        .then(r => { setHostResults(r.data); setShowDropdown(r.data.length > 0); })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [hostQuery]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/guests/register`, {
        name: name.trim(),
        email: email.trim() || null,
        host_id: selectedHost?.id || null,
        purpose: purpose.trim() || null,
        company: company.trim() || null,
      });
      if (res.data.was_pre_registered) {
        setWasPreRegistered(true);
        setPreRegHost(res.data.host_name || "");
      }
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setName(""); setEmail(""); setCompany(""); setPurpose("");
    setHostQuery(""); setSelectedHost(null); setSuccess(false);
    setWasPreRegistered(false); setPreRegHost("");
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-8 text-center border border-border" data-testid="guest-register-success">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-emerald-50 mb-6 mx-auto">
            <Check className="h-10 w-10 text-emerald-600" strokeWidth={2.5} />
          </div>
          <h1 className="font-heading font-bold text-2xl mb-2">Welcome, {name}!</h1>
          {wasPreRegistered ? (
            <>
              <p className="text-muted-foreground mb-2">We found your pre-registration. You're all set!</p>
              {preRegHost && (
                <p className="text-sm text-muted-foreground mb-6"><strong>{preRegHost}</strong> has been notified.</p>
              )}
            </>
          ) : (
            <>
              <p className="text-muted-foreground mb-2">You've been checked in successfully.</p>
              {selectedHost && (
                <p className="text-sm text-muted-foreground mb-6">
                  <strong>{selectedHost.name}</strong> has been notified of your arrival.
                </p>
              )}
            </>
          )}
          <Button onClick={resetForm} variant="outline" className="rounded-none" data-testid="register-another-btn">
            Register Another Guest
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-lg w-full p-8 border border-border" data-testid="guest-register-form">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 mb-4">
            <UserPlus className="h-7 w-7 text-primary" />
          </div>
          <h1 className="font-heading font-bold text-2xl" data-testid="guest-register-title">
            Welcome to The Rig
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Please register your visit</p>
        </div>

        <div className="space-y-5">
          <div>
            <Label>Full Name *</Label>
            <Input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="Your full name"
              data-testid="guest-name-input"
            />
          </div>

          <div>
            <Label>Email</Label>
            <Input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com (optional)"
              data-testid="guest-email-input"
            />
          </div>

          <div>
            <Label>Company</Label>
            <Input
              value={company} onChange={e => setCompany(e.target.value)}
              placeholder="Company name (optional)"
              data-testid="guest-company-input"
            />
          </div>

          <div className="relative" ref={dropdownRef}>
            <Label>Who are you visiting?</Label>
            {selectedHost ? (
              <div className="flex items-center justify-between p-3 border rounded-md bg-muted/50" data-testid="selected-host">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{selectedHost.name}</span>
                  {selectedHost.company_name && (
                    <span className="text-xs text-muted-foreground">{selectedHost.company_name}</span>
                  )}
                </div>
                <Button size="sm" variant="ghost" onClick={() => { setSelectedHost(null); setHostQuery(""); }} data-testid="clear-host-btn">
                  Change
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    value={hostQuery} onChange={e => setHostQuery(e.target.value)}
                    placeholder="Start typing a name..."
                    data-testid="host-search-input"
                  />
                </div>
                {showDropdown && (
                  <div className="absolute z-20 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="host-results-dropdown">
                    {hostResults.map(h => (
                      <button
                        key={h.id}
                        className="w-full text-left px-4 py-3 hover:bg-muted/50 border-b last:border-0 transition-colors"
                        onClick={() => { setSelectedHost(h); setHostQuery(h.name); setShowDropdown(false); }}
                        data-testid={`host-option-${h.id}`}
                      >
                        <div className="font-medium text-sm">{h.name}</div>
                        {h.company_name && <div className="text-xs text-muted-foreground">{h.company_name}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <Label>Purpose of Visit</Label>
            <Input
              value={purpose} onChange={e => setPurpose(e.target.value)}
              placeholder="e.g. Meeting, Delivery, Interview..."
              data-testid="guest-purpose-input"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
            className="w-full bg-foreground text-background hover:bg-foreground/90 rounded-none h-12 text-base"
            data-testid="guest-register-submit"
          >
            {submitting ? "Registering..." : "Check In"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
